/**
 * Individual KPI cell for Cached Performance (CR-4751).
 *
 * Mirrors DefaultTab KPIMetricsSection cell styling (label, big value, footer
 * with delta indicator) and adds a sparkline mini-chart from the trend series.
 */
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react'

import type {
  DashboardSnapshot, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { asNumber, formatValueByUnit, percentDelta, toChartSeries } from './helpers'

interface CachedKPICellProps {
  label: string
  snapshot: DashboardSnapshot | undefined
  trend: TrendPoint[] | undefined
  lowerIsBetter?: boolean
  emphasis?: 'destructive' | 'plain'
}

function isPartialPeriod(snapshot: DashboardSnapshot): boolean {
  if (!snapshot.period_end) return false
  const periodEnd = new Date(snapshot.period_end + 'T23:59:59')
  return periodEnd > new Date()
}

export function CachedKPICell({
  label, snapshot, trend, lowerIsBetter, emphasis = 'plain',
}: CachedKPICellProps) {
  const { formatCurrency } = useFormatters()
  const labelClass = 'text-xs text-muted-foreground font-medium uppercase tracking-wide leading-tight h-8 flex items-center justify-center text-center px-1'
  const valueClass = 'h-16 flex items-center justify-center w-full'
  const sparkClass = 'h-8 w-full'
  const footClass = 'h-6 flex items-center justify-center'

  if (!snapshot) {
    return (
      <div className="flex flex-col items-center">
        <p className={labelClass}>{label}</p>
        <div className={valueClass}>
          <p className="text-2xl font-semibold text-muted-foreground">—</p>
        </div>
        <div className={sparkClass} />
        <div className={footClass}>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="w-3 h-3" /> Building history
          </span>
        </div>
      </div>
    )
  }

  const trendPoints = trend ?? []
  const sparkSeries = toChartSeries(trendPoints)
  const current = asNumber(snapshot.metric_value)
  const previousPoint = trendPoints.length >= 2 ? trendPoints[trendPoints.length - 2] : undefined
  const hasPrior = previousPoint !== undefined
  const previous = hasPrior ? asNumber(previousPoint!.metric_value) : null
  const delta = previous !== null && previous !== 0
    ? percentDelta(current, previous)
    : null

  let deltaIcon = <Minus className="w-3 h-3" />
  let deltaColor = 'text-muted-foreground'
  if (delta !== null && delta !== 0) {
    const improved = lowerIsBetter ? delta < 0 : delta > 0
    if (improved) {
      deltaIcon = <TrendingUp className="w-3 h-3" />
      deltaColor = 'text-green-600'
    } else {
      deltaIcon = <TrendingDown className="w-3 h-3" />
      deltaColor = 'text-red-600'
    }
  }

  const formatted = formatValueByUnit(snapshot.metric_value, snapshot.metric_unit, formatCurrency)
  let sparkColor = '#6b7280'
  if (delta !== null && delta !== 0) {
    const improved = lowerIsBetter ? delta < 0 : delta > 0
    sparkColor = improved ? '#10b981' : '#ef4444'
  }

  const renderFooter = () => {
    if (!hasPrior) {
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="w-3 h-3" /> no prior
        </span>
      )
    }
    if (previous === 0 && current === 0) {
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Minus className="w-3 h-3" /> unchanged
        </span>
      )
    }
    if (previous === 0 && current !== 0) {
      const improved = lowerIsBetter ? current < 0 : current > 0
      const color = improved ? 'text-green-600' : 'text-red-600'
      const icon = improved ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />
      return (
        <span className={`text-xs flex items-center gap-1 ${color}`}>
          {icon} new vs 0 prior
        </span>
      )
    }
    return (
      <span className={`text-xs flex items-center gap-1 ${deltaColor}`}>
        {deltaIcon} {delta!.toFixed(1)}% vs prior
      </span>
    )
  }

  const partial = isPartialPeriod(snapshot)

  return (
    <div className="flex flex-col items-center">
      <p className={labelClass}>
        {label}
        {partial && (
          <span
            className="ml-1 inline-block px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-semibold align-middle"
            title="This period hasn't ended yet — value is partial"
          >
            PARTIAL
          </span>
        )}
      </p>
      <div className={valueClass}>
        {emphasis === 'destructive' ? (
          <div className="bg-destructive text-destructive-foreground px-3 py-2 rounded-md">
            <p className="text-2xl font-bold leading-none">{formatted}</p>
          </div>
        ) : (
          <div className="text-2xl font-bold leading-none text-center">{formatted}</div>
        )}
      </div>
      <div className={sparkClass}>
        {sparkSeries.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkSeries} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={sparkColor}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : null}
      </div>
      <div className={footClass}>{renderFooter()}</div>
    </div>
  )
}
